import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Users, Briefcase, CalendarCheck, BookOpen, Bell, LogOut, Menu, X, Award, User, CheckCircle, FileText, Sparkles } from 'lucide-react';
import axios from 'axios';

const PortalLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Try to parse user, default to null if invalid
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('portalUser')) || JSON.parse(localStorage.getItem('admin'));
  } catch(e){}

  const isMainAdmin = location.pathname.includes('/dashboard/admin') || (user && !user.role); // main admin doesn't have role field, just admin token
  const role = isMainAdmin ? 'Admin' : user?.role || 'Intern';

  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  React.useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
        if (!token) return;
        const endpoint = isMainAdmin ? '/api/portal/admin/notifications' : '/api/portal/shared/notifications';
        const res = await axios.get(`${window.API_URL}${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (Array.isArray(res.data)) {
          setNotifications(res.data);
        } else {
          console.error("Expected notifications array but got:", res.data);
          setNotifications([]);
        }
      } catch (err) {
        console.error("Failed to fetch notifications");
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [isMainAdmin]);

  const unreadCount = Array.isArray(notifications) ? notifications.filter(n => !n.read).length : 0;

  const handleMarkAsRead = async (id) => {
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      await axios.put(`${window.API_URL}/api/portal/shared/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(notifications)) {
        setNotifications(notifications.map(n => n._id === id ? { ...n, read: true } : n));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalUser');
    navigate('/portal/login'); // Redirect to login
  };

  const menuItems = {
    Admin: [
      { path: '/dashboard/admin', icon: <LayoutDashboard />, label: 'Overview' },
      { path: '/dashboard/admin/cms', icon: <Sparkles />, label: 'Manage Website CMS' },
      { path: '/dashboard/admin/users', icon: <Users />, label: 'Manage Users' },
      { path: '/dashboard/admin/tasks', icon: <Briefcase />, label: 'Manage Tasks' },
      { path: '/dashboard/admin/attendance', icon: <CalendarCheck />, label: 'Attendance Log' },
      { path: '/dashboard/admin/team', icon: <Users />, label: 'Manage Team' },
      { path: '/dashboard/admin/projects', icon: <Briefcase />, label: 'Website Portfolio' },
      { path: '/dashboard/admin/internal-projects', icon: <Briefcase />, label: 'Projects Management' },
      { path: '/dashboard/admin/products', icon: <BookOpen />, label: 'Manage Products' },
      { path: '/dashboard/admin/plans', icon: <Award />, label: 'Manage Plans' },
      { path: '/dashboard/admin/blogs', icon: <FileText />, label: 'Manage Blogs' },
      { path: '/dashboard/admin/bot', icon: <Bell />, label: 'Train Bot' },
      { path: '/dashboard/admin/profile', icon: <User />, label: 'My Profile' },
    ],
    Intern: [
      { path: '/dashboard/intern', icon: <LayoutDashboard />, label: 'Overview' },
      { path: '/dashboard/intern/tasks', icon: <Briefcase />, label: 'Daily Tasks' },
      { path: '/dashboard/intern/projects', icon: <Briefcase />, label: 'Projects' },
      { path: '/dashboard/intern/attendance', icon: <CalendarCheck />, label: 'Attendance' },
      { path: '/dashboard/intern/learning', icon: <BookOpen />, label: 'Learning' },
      { path: '/dashboard/intern/performance', icon: <Award />, label: 'Performance' },
      { path: '/dashboard/intern/profile', icon: <User />, label: 'My Profile' },
    ],
    Employee: [
      { path: '/dashboard/employee', icon: <LayoutDashboard />, label: 'Overview' },
      { path: '/dashboard/employee/tasks', icon: <Briefcase />, label: 'Assigned Tasks' },
      { path: '/dashboard/employee/projects', icon: <Briefcase />, label: 'Projects' },
      { path: '/dashboard/employee/attendance', icon: <CalendarCheck />, label: 'Attendance' },
      { path: '/dashboard/employee/profile', icon: <User />, label: 'My Profile' },
    ]
  };

  const employeeRoles = ['Employee', 'Manager', 'Team Lead', 'Developer', 'Designer', 'CEO'];
  const navLinks = menuItems[role] || (employeeRoles.includes(role) ? menuItems.Employee : []);

  return (
    <div className="flex h-screen bg-gray-50 text-gray-900 font-sans overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-100/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static top-0 left-0 h-full w-64 shrink-0 bg-gray-50 border-r border-gray-200 z-30 transform lg:transform-none transition-transform duration-300 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200 bg-gray-100/50">
          <Link to="/" className="flex items-center hover:opacity-90 transition-opacity py-1">
            <img 
              src="/logo.png" 
              alt="Beta Communes Logo" 
              className="h-9 w-auto max-h-10 object-contain" 
              onError={(e) => { e.target.style.display = 'none'; }} 
            />
          </Link>
          <button className="lg:hidden text-gray-600 hover:text-gray-900" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path || location.pathname.startsWith(link.path + '/');
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-[#f06600]/20 text-[#f06600] font-medium' 
                    : 'text-gray-600 hover:bg-white shadow-sm hover:text-gray-800'
                }`}
              >
                <span className={`mr-3 ${isActive ? 'text-[#f06600]' : 'text-gray-500'}`}>
                  {React.cloneElement(link.icon, { className: "w-5 h-5" })}
                </span>
                {link.label}
              </Link>
            )
          })}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-100/50">
          <button 
            onClick={handleLogout}
            className="flex items-center w-full px-3 py-2 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col w-full h-full relative">
        {/* Top Navbar */}
        <header className="h-16 flex items-center justify-between px-4 lg:px-8 border-b border-gray-200 bg-white/80 backdrop-blur-md z-10 sticky top-0">
          <div className="flex items-center">
            <button 
              className="mr-4 lg:hidden text-gray-600 hover:text-gray-900"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-medium text-gray-800 mr-6 capitalize">
              {role} Dashboard
            </h1>
            {location.pathname !== `/dashboard/${role.toLowerCase()}` && (
              <Link 
                to={`/dashboard/${role.toLowerCase()}`} 
                className="hidden md:flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 bg-white shadow-sm hover:bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 transition-colors mr-2"
              >
                ⬅ Back to Dashboard
              </Link>
            )}
            <Link 
              to="/" 
              className="hidden md:flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 bg-white shadow-sm hover:bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 transition-colors"
            >
              ⬅ Back to Website
            </Link>
          </div>
          
          <div className="flex items-center space-x-4 relative">
            <button 
              className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            <AnimatePresence>
              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)}></div>
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute top-full right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden"
                  >
                    <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                      <h3 className="font-bold text-gray-900">Notifications</h3>
                      <span className="text-xs bg-[#f06600]/20 text-[#f06600] px-2 py-1 rounded font-medium">{unreadCount} New</span>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                          {Array.isArray(notifications) && notifications.map(notif => (
                            <div 
                              key={notif._id} 
                              className={`p-4 transition-colors hover:bg-gray-50 ${!notif.read ? 'bg-orange-50/30' : ''}`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className={`text-sm font-semibold ${!notif.read ? 'text-gray-900' : 'text-gray-700'}`}>{notif.title}</h4>
                                  <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                                  <p className="text-[10px] text-gray-400 mt-2">{new Date(notif.createdAt).toLocaleString()}</p>
                                </div>
                                {!notif.read && (
                                  <button 
                                    onClick={() => handleMarkAsRead(notif._id)}
                                    className="text-gray-400 hover:text-green-500 transition-colors p-1"
                                    title="Mark as read"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                          <Bell className="w-8 h-8 text-gray-300 mb-2" />
                          <p className="text-sm">No notifications yet</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            <div className="flex items-center pl-4 border-l border-gray-200">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#f06600] to-orange-400 flex items-center justify-center text-sm font-bold shadow-lg">
                {(user?.fullName || user?.username || 'A')[0].toUpperCase()}
              </div>
              <span className="ml-3 text-sm font-medium hidden md:block">
                {user?.fullName || user?.username || 'Admin'}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-100 p-4 lg:p-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="h-full"
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default PortalLayout;
